import { FormEvent, useEffect, useState } from 'react';
import { authzApi, type Policy } from '../../api/authz';

export function PoliciesPage() {
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [name, setName] = useState('');
  const [effect, setEffect] = useState<'allow' | 'deny'>('allow');
  const [resource, setResource] = useState('*');
  const [action, setAction] = useState('*');
  const [priority, setPriority] = useState(10);
  const [error, setError] = useState('');

  async function load() {
    try {
      setPolicies(await authzApi.listPolicies());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Load failed');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    try {
      await authzApi.createPolicy({ name, effect, resource, action, priority, enabled: true });
      setName('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Create failed');
    }
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h1 className="text-2xl font-semibold text-slate-900">Policies</h1>
      <p className="mt-1 text-sm text-slate-600">Attribute-based policies with priority and conflict resolution.</p>
      {error && <p className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}

      <form onSubmit={onCreate} className="mt-6 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 md:grid-cols-3">
        <input required placeholder="Policy name" value={name} onChange={(e) => setName(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" />
        <select value={effect} onChange={(e) => setEffect(e.target.value as 'allow' | 'deny')} className="rounded-lg border border-slate-300 px-3 py-2">
          <option value="allow">Allow</option>
          <option value="deny">Deny</option>
        </select>
        <input type="number" value={priority} onChange={(e) => setPriority(Number(e.target.value))} className="rounded-lg border border-slate-300 px-3 py-2" aria-label="Priority" />
        <input value={resource} onChange={(e) => setResource(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" placeholder="Resource" />
        <input value={action} onChange={(e) => setAction(e.target.value)} className="rounded-lg border border-slate-300 px-3 py-2" placeholder="Action" />
        <button type="submit" className="rounded-lg bg-slate-900 px-4 py-2 text-white">Create policy</button>
      </form>

      <ul className="mt-6 space-y-3">
        {policies.map((p) => (
          <li key={p.id} className="flex items-start justify-between rounded-xl border border-slate-200 bg-white p-4">
            <div>
              <p className="font-medium text-slate-900">{p.name}</p>
              <p className="text-sm text-slate-600">{p.effect.toUpperCase()} · {p.resource}:{p.action} · priority {p.priority}</p>
            </div>
            <button type="button" onClick={() => void authzApi.deletePolicy(p.id).then(load)} className="text-sm text-red-600 hover:underline">Delete</button>
          </li>
        ))}
        {policies.length === 0 && <li className="text-sm text-slate-600">No policies defined.</li>}
      </ul>
    </div>
  );
}
